import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { basename, dirname, join, resolve } from 'node:path';
import { emptyCatalog, own, type Catalogs } from './catalog';
import { targetLocales, type ResolvedConfig } from './config';
import { escapeXml, inlineToMessage, messageToInline, unescapeXml } from './inline';
import { androidValuesDir, toAndroidXml, toIosStrings } from './mobile';
import { parsePo, toPo } from './po';
import { isLocaleTag, projectLocale, suggestTag } from './tag';
import { structureMatches } from './translate';

export type ExchangeFormat = 'xliff' | 'csv' | 'po';
export type MobileFormat = 'android-xml' | 'ios-strings';
export type ExportFormat = ExchangeFormat | MobileFormat;

export function isMobileFormat(format: ExportFormat): format is MobileFormat {
  return format === 'android-xml' || format === 'ios-strings';
}

export interface ExportOptions {
  locales?: string[];
  format?: ExportFormat;
  out?: string;
  missing?: boolean;
  origins?: Record<string, string[]>;
}

export interface ExportedFile {
  locale: string;
  path: string;
  total: number;
  untranslated: number;
}

export interface ExportResult {
  format: ExportFormat;
  dir: string;
  files: ExportedFile[];
}

export interface ImportOptions {
  locale?: string;
  overwrite?: boolean;
  dryRun?: boolean;
}

export interface ImportResult {
  imported: Record<string, string[]>;
  rejected: Record<string, string[]>;
  skipped: Record<string, string[]>;
  unknown: Record<string, string[]>;
  unmatched: { file: string; locale: string; reason: 'undeclared' | 'invalid' }[];
  mapped: { file: string; from: string; to: string }[];
  sources: Record<string, Record<string, string>>;
}

// One file per target locale: source text + current translation
export function exportCatalogs(
  cfg: ResolvedConfig,
  catalogs: Catalogs,
  options: ExportOptions = {},
): ExportResult {
  const format = options.format ?? 'xliff';
  const dir = resolve(cfg.root, options.out ?? 'verbaly-export');
  const source = catalogs[cfg.sourceLocale] ?? {};
  const targets = targetLocales(cfg, options.locales);
  if (isMobileFormat(format)) {
    return exportMobile(cfg, catalogs, format, dir, source, targets);
  }

  const files: ExportedFile[] = [];
  const origins = options.origins;
  mkdirSync(dir, { recursive: true });
  for (const locale of targets) {
    const catalog = catalogs[locale] ?? emptyCatalog();
    const all = Object.keys(source)
      .filter((key) => source[key])
      .sort()
      .map((key) => ({
        key,
        source: source[key]!,
        target: own(catalog, key) ?? '',
        // own entries: a key named constructor would find Object's function and crash the writer
        location: origins && Object.hasOwn(origins, key) ? origins[key] : undefined,
      }));
    const untranslated = all.filter((entry) => !entry.target);
    const entries = options.missing ? untranslated : all;

    const ext = format === 'xliff' ? 'xlf' : format;
    const path = join(dir, `${locale}.${ext}`);
    const content =
      format === 'csv'
        ? toCsv(entries)
        : format === 'po'
          ? toPo(cfg.sourceLocale, locale, entries)
          : toXliff(cfg.sourceLocale, locale, entries);
    writeFileSync(path, content);
    files.push({
      locale,
      path,
      total: entries.length,
      untranslated: untranslated.length,
    });
  }
  return { format, dir, files };
}

// native resources: source locale is the platform default, untranslated keys are skipped
function exportMobile(
  cfg: ResolvedConfig,
  catalogs: Catalogs,
  format: MobileFormat,
  dir: string,
  source: Record<string, string>,
  targets: string[],
): ExportResult {
  const keys = Object.keys(source)
    .filter((key) => source[key])
    .sort();
  const files: ExportedFile[] = [];
  for (const locale of [cfg.sourceLocale, ...targets]) {
    const catalog = locale === cfg.sourceLocale ? source : (catalogs[locale] ?? {});
    const entries = keys
      .map((key) => ({ key, text: own(catalog, key) ?? '' }))
      .filter((entry) => entry.text);
    const relative =
      format === 'android-xml'
        ? join(androidValuesDir(locale, cfg.sourceLocale), 'strings.xml')
        : join(`${locale}.lproj`, 'Localizable.strings');
    const path = join(dir, relative);
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, format === 'android-xml' ? toAndroidXml(entries) : toIosStrings(entries));
    files.push({
      locale,
      path,
      total: entries.length,
      untranslated: keys.length - entries.length,
    });
  }
  return { format, dir, files };
}

// fills catalogs from translated files
export function importCatalogs(
  cfg: ResolvedConfig,
  catalogs: Catalogs,
  files: string[],
  options: ImportOptions = {},
): ImportResult {
  const source = catalogs[cfg.sourceLocale] ?? {};
  const result: ImportResult = {
    imported: {},
    rejected: {},
    skipped: {},
    unknown: {},
    unmatched: [],
    mapped: [],
    sources: {},
  };

  for (const file of files) {
    const parsed = parseExchangeFile(file, options.locale);
    const detected = parsed.locale;
    if (!/^[a-zA-Z]{2,3}([-_][a-zA-Z0-9]+)*$/.test(detected)) {
      throw new Error(
        `[verbaly] ${file}: "${detected}" doesn't look like a locale, pass --locale <id>.`,
      );
    }
    const locale = projectLocale(detected, cfg.locales) ?? newLocale(cfg, detected);
    if (locale === cfg.sourceLocale) {
      throw new Error(
        `[verbaly] ${file} targets the source locale "${locale}": import fills translations, not the source. Pass --locale if the detection is wrong.`,
      );
    }
    // a pt_BR catalog next to pt-BR, or one a declared list never loads, is a language nobody sees
    if (locale === undefined) {
      const near = isLocaleTag(detected) ? detected : suggestTag(detected);
      result.unmatched.push(
        near
          ? { file, locale: near, reason: 'undeclared' }
          : { file, locale: detected, reason: 'invalid' },
      );
      continue;
    }
    if (locale !== detected) result.mapped.push({ file, from: detected, to: locale });
    const catalog = (catalogs[locale] ??= emptyCatalog());
    for (const [key, text] of Object.entries(parsed.entries)) {
      if (!text.trim()) continue;
      const from = own(source, key);
      if (!from) {
        (result.unknown[locale] ??= []).push(key);
        continue;
      }
      if (own(catalog, key) && !options.overwrite) {
        (result.skipped[locale] ??= []).push(key);
        continue;
      }
      if (!structureMatches(from, text)) {
        (result.rejected[locale] ??= []).push(key);
        continue;
      }
      if (!options.dryRun) catalog[key] = text;
      (result.imported[locale] ??= []).push(key);
      const seen = parsed.sources[key];
      if (seen) (result.sources[locale] ??= {})[key] = seen;
    }
  }
  return result;
}

interface ExchangeEntry {
  key: string;
  source: string;
  target: string;
  location?: string[];
}

interface ParsedFile {
  locale: string;
  entries: Record<string, string>;
  // the source text the translator saw, when the format carries one
  sources: Record<string, string>;
}

export function parseExchangeFile(file: string, localeOverride?: string): ParsedFile {
  const content = readFileSync(file, 'utf8');
  if (/\.(xlf|xliff)$/i.test(file)) {
    const parsed = parseXliff(content);
    const locale = localeOverride ?? parsed.locale;
    if (!locale) {
      throw new Error(`[verbaly] ${file} has no trgLang/target-language, pass --locale <id>.`);
    }
    return { locale, entries: parsed.entries, sources: parsed.sources };
  }
  if (/\.csv$/i.test(file)) {
    const locale = localeOverride ?? basename(file).replace(/\.csv$/i, '');
    return { locale, ...parseCsv(content) };
  }
  if (/\.po$/i.test(file)) {
    const parsed = parsePo(content);
    const locale = localeOverride ?? parsed.locale ?? basename(file).replace(/\.po$/i, '');
    return { locale, entries: parsed.entries, sources: parsed.sources };
  }
  throw new Error(`[verbaly] ${file}: unsupported format, expected .xlf, .xliff, .csv or .po.`);
}

// XLIFF 2.0 (writes) / 2.0 + 1.2 (reads); params/tags travel as <ph>/<pc> inline codes
function toXliff(sourceLocale: string, locale: string, entries: ExchangeEntry[]): string {
  const units = entries
    .map(({ key, source, target, location }) =>
      [
        `    <unit id="${escapeXml(key)}">`,
        ...(location?.length
          ? [
              '      <notes>',
              ...location.map(
                (file) => `        <note category="location">${escapeXml(file)}</note>`,
              ),
              '      </notes>',
            ]
          : []),
        `      <segment state="${target ? 'translated' : 'initial'}">`,
        `        <source>${messageToInline(source)}</source>`,
        `        <target>${messageToInline(target)}</target>`,
        '      </segment>',
        '    </unit>',
      ].join('\n'),
    )
    .join('\n');
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<xliff xmlns="urn:oasis:names:tc:xliff:document:2.0" version="2.0" srcLang="${escapeXml(sourceLocale)}" trgLang="${escapeXml(locale)}">`,
    `  <file id="verbaly" original="${escapeXml(`${locale}.json`)}">`,
    units,
    '  </file>',
    '</xliff>',
    '',
  ].join('\n');
}

// with no list written down, a language exists by having a catalog, so a file may start one
function newLocale(cfg: ResolvedConfig, detected: string): string | undefined {
  if (cfg.localesDeclared) return undefined;
  return isLocaleTag(detected) ? detected : suggestTag(detected);
}

function parseXliff(content: string): {
  locale?: string;
  entries: Record<string, string>;
  sources: Record<string, string>;
} {
  const locale =
    /\btrgLang\s*=\s*"([^"]+)"/.exec(content)?.[1] ??
    /\btarget-language\s*=\s*"([^"]+)"/.exec(content)?.[1];
  const entries = Object.create(null) as Record<string, string>;
  const sources = Object.create(null) as Record<string, string>;
  const UNIT = /<(?:trans-)?unit\b([^>]*)>([\s\S]*?)<\/(?:trans-)?unit>/g;
  for (const match of content.matchAll(UNIT)) {
    const id = /\bid\s*=\s*"([^"]*)"/.exec(match[1]!)?.[1];
    if (!id) continue;
    const target = /<target\b[^>]*>([\s\S]*?)<\/target>/.exec(match[2]!)?.[1];
    entries[unescapeXml(id)] = target === undefined ? '' : inlineToMessage(stripCdata(target));
    const source = /<source\b[^>]*>([\s\S]*?)<\/source>/.exec(match[2]!)?.[1];
    if (source !== undefined) sources[unescapeXml(id)] = inlineToMessage(stripCdata(source));
  }
  return { locale, entries, sources };
}

function stripCdata(text: string): string {
  return text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1');
}

// CSV (RFC 4180); import reads columns by header name, so location round-trips inert
function toCsv(entries: ExchangeEntry[]): string {
  const rows = entries.map(
    ({ key, source, target, location }) =>
      `${csvField(key)},${csvField(source)},${csvField(target)},${csvField(location?.join('; ') ?? '')}`,
  );
  return ['key,source,target,location', ...rows, ''].join('\r\n');
}

function csvField(text: string): string {
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function parseCsv(content: string): {
  entries: Record<string, string>;
  sources: Record<string, string>;
} {
  const rows = csvRows(content);
  const header = rows[0]?.map((cell) => cell.trim().toLowerCase()) ?? [];
  const keyCol = header.indexOf('key');
  const targetCol = header.indexOf('target');
  const sourceCol = header.indexOf('source');
  if (keyCol === -1 || targetCol === -1) {
    throw new Error('[verbaly] CSV needs a header row with "key" and "target" columns.');
  }
  const entries = Object.create(null) as Record<string, string>;
  const sources = Object.create(null) as Record<string, string>;
  for (const row of rows.slice(1)) {
    const key = row[keyCol];
    if (!key) continue;
    entries[key] = row[targetCol] ?? '';
    const source = sourceCol === -1 ? undefined : row[sourceCol];
    if (source) sources[key] = source;
  }
  return { entries, sources };
}

function csvRows(content: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < content.length; i++) {
    const char = content[i]!;
    if (quoted) {
      if (char === '"') {
        if (content[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && content[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((cell) => cell !== '')) rows.push(row);
      row = [];
    } else {
      field += char;
    }
  }
  row.push(field);
  if (row.some((cell) => cell !== '')) rows.push(row);
  return rows;
}
